import { useCallback, useRef } from 'react';
import { fetchCurrentUserData } from '../../../utils/ApiCall';
import {
    getEffectiveFactorDataModel,
    normalizeUserFactorModel,
} from '../../../utils/factorDataOptions';
import { factorContextsUseSameSelection } from '../dashboardUtils';
import { getRiskCommandsOrThrow } from '../orchestration/riskCommandRef';
import { coreLoadPublicKey } from './useCoreLoadBootstrap';
import { durableFactorSetting } from './useWatchlistWorkflow';

// Saving the published vendor's model (an Axioma horizon, for example) moves
// the published Watchlist to it in the same server transaction, so the page's
// accepted factor context is superseded at once and the server rejects reads
// that still name it. The saved model is loaded the way a Watchlist
// publication is: one staged refresh of the published vendor and model on the
// server's published date, retried while it is not ready. The previous view
// stays until that refresh commits, and the superseded context is never
// restored, because the server no longer serves it.
export const FOLLOW_RETRY_DELAYS_MS = Object.freeze([0, 2000, 5000, 15000]);
// Superseded reads arrive in bursts; one saved-setting read answers them.
export const STALE_CONTEXT_RESYNC_INTERVAL_MS = 30 * 1000;
export const FOLLOW_FAILED_MESSAGE = 'Your factor model was saved, but its risk data could not load. '
    + 'Your previous view is still shown. Reload the page to try again.';

const wait = (delayMs) => new Promise((resolve) => {
    setTimeout(resolve, delayMs);
});

function readSavedSelection({ selectedFactorModelRef, selectedAxiomaModelRef, selectedBarraModelRef }) {
    const factorModel = selectedFactorModelRef.current;
    return {
        factorModel,
        factorDataModel: getEffectiveFactorDataModel(
            factorModel,
            selectedAxiomaModelRef.current,
            selectedBarraModelRef.current,
        ) ?? '',
    };
}

const selectionKey = ({ factorModel, factorDataModel }) => `${factorModel}|${factorDataModel}`;

export default function usePublishedFactorSettingFollow(options) {
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const stateRef = useRef({
        running: false,
        failed: null,
        resyncInFlight: false,
        resyncStartedAt: -Infinity,
    });

    // `retryFailed` lets a superseded read retry a save that failed, with one
    // quiet attempt; otherwise only a later save retries it, never a re-render.
    const followPublishedFactorSetting = useCallback(async ({ retryFailed = false } = {}) => {
        const state = stateRef.current;
        const {
            userUid,
            userData,
            acceptedFactorContextRef,
            exclusiveCoreLoadOwnerRef,
            riskCommandsRef,
        } = optionsRef.current;
        const saved = readSavedSelection(optionsRef.current);
        const savedKey = selectionKey(saved);
        if (
            state.running
            || !userUid
            || !acceptedFactorContextRef.current
            || exclusiveCoreLoadOwnerRef.current
            || factorContextsUseSameSelection(saved, acceptedFactorContextRef.current)
            || (!retryFailed && state.failed?.key === savedKey && state.failed.userData === userData)
        ) {
            return false;
        }
        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'usePublishedFactorSettingFollow',
            ['refreshRiskSurface', 'refreshSelectedOverview'],
        );
        const sameUser = () => optionsRef.current.userUid === userUid;
        const newerSave = () => (
            !sameUser() || selectionKey(readSavedSelection(optionsRef.current)) !== savedKey
        );
        // A newer save, a publication, or a publication that already loaded
        // the saved model ends this follow without a message.
        const nothingToFollow = () => (
            newerSave()
            || Boolean(optionsRef.current.exclusiveCoreLoadOwnerRef.current)
            || factorContextsUseSameSelection(saved, optionsRef.current.acceptedFactorContextRef.current)
        );
        // A save that has not failed yet gets every attempt and its notice.
        const quietRetry = retryFailed
            && state.failed?.key === savedKey
            && state.failed.userData === userData;
        state.running = true;
        state.failed = null;
        optionsRef.current.setFactorDataUpdateLoading(true);
        const visualToken = optionsRef.current.beginCoreLoadVisual();
        let loadResult = null;
        let followed = false;
        try {
            for (const delayMs of quietRetry ? [0] : optionsRef.current.retryDelaysMs ?? FOLLOW_RETRY_DELAYS_MS) {
                // Waiting a turn also lets this render's bootstrap suspension
                // finish before the fence below is set.
                await wait(delayMs);
                if (nothingToFollow()) break;
                // Fences price refreshes out of this load.
                optionsRef.current.coreLoadInFlightRef.current = true;
                try {
                    const result = await commands.refreshRiskSurface({
                        reason: 'factor_data_refresh',
                        factorVendorOverride: normalizeUserFactorModel(saved.factorModel),
                        factorDataModelOverride: saved.factorDataModel,
                        factorSnapshotDateOverride: null,
                        requireFreshMatrices: true,
                        preserveExistingOnMatrixFailure: true,
                        includeSelectedOverview: true,
                        includeOptimizerSummaries: true,
                    });
                    // The refresh has published its rows, so the accepted
                    // context describes them even if a newer save follows next.
                    if (sameUser() && optionsRef.current.promoteFactorContext(result, 'settings_changed')) {
                        loadResult = result;
                        break;
                    }
                } catch (error) {
                    console.warn('The saved factor model could not load yet:', error);
                }
            }
            if (loadResult && !newerSave()) {
                followed = true;
                optionsRef.current.markPendingRefreshFromLoadResult(loadResult);
                optionsRef.current.lastCoreLoadKeyRef.current = coreLoadPublicKey(
                    userUid,
                    saved.factorModel,
                    saved.factorDataModel,
                );
                try {
                    await optionsRef.current.requestPosSizingContextUpdate({
                        reason: 'factor_data_refresh',
                        allowParentPublication: true,
                    });
                } catch (error) {
                    // Position Sizing reports its own freshness; the risk data is loaded.
                    console.warn('Position Sizing could not check the saved factor model:', error);
                }
                optionsRef.current.onNotice('Factor data loaded.', 'success');
            } else if (!loadResult && !nothingToFollow()) {
                state.failed = { key: savedKey, userData: optionsRef.current.userData };
                if (!quietRetry) optionsRef.current.onNotice(FOLLOW_FAILED_MESSAGE, 'error');
            }
        } finally {
            state.running = false;
            if (sameUser()) {
                optionsRef.current.coreLoadInFlightRef.current = false;
                optionsRef.current.endCoreLoadVisual(visualToken);
                optionsRef.current.setFactorDataUpdateLoading(false);
                // A price refresh queued behind this load runs now; it ends
                // with its own selected Overview refresh.
                const queuedPriceRefresh = followed ? optionsRef.current.consumeQueuedPriceRefresh() : null;
                if (queuedPriceRefresh) {
                    void optionsRef.current.refreshPriceDataRef.current?.(queuedPriceRefresh);
                } else {
                    commands.refreshSelectedOverview({ reason: 'factor_data_refresh' });
                }
            }
        }
        return Boolean(loadResult);
    }, []);

    // A risk read named a factor context the published Watchlist no longer
    // uses: another tab or device saved a model, or the read started before
    // this page's own save. The server keeps the published vendor's saved
    // setting equal to the published Watchlist's, so follow that setting.
    const handleFactorContextChanged = useCallback(async () => {
        const state = stateRef.current;
        const startedAt = Date.now();
        if (state.resyncInFlight || startedAt - state.resyncStartedAt < STALE_CONTEXT_RESYNC_INTERVAL_MS) {
            return;
        }
        state.resyncInFlight = true;
        state.resyncStartedAt = startedAt;
        try {
            optionsRef.current.onUserDataChange?.(durableFactorSetting(await fetchCurrentUserData()));
            // A changed setting is followed once the page reads it; an
            // unchanged one that failed to load is retried now.
            await followPublishedFactorSetting({ retryFailed: true });
        } catch (error) {
            console.warn('Could not read the saved factor setting:', error);
        } finally {
            state.resyncInFlight = false;
        }
    }, [followPublishedFactorSetting]);

    return { followPublishedFactorSetting, handleFactorContextChanged };
}
